/* eslint-disable max-lines-per-function */

import { noop } from 'lodash';
import { useMemo } from 'react';

import { MedicalImaging, MedicalImagingQuery } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { dateFormatter, Ordering } from '@dreryk/react-common';
import {
   colorsMatrix,
   createDynamicColumnsWidth,
   LinkSize,
   TableColumnBackgroundColorResolver,
   TableColumnConfiguration,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';

import { ConfidentialMask, getConfidentialAccessButton } from '~libs.desktop.shared.medical-data-confidentiality';
import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';
import { PatientMedicalInformationRemoveModalOpener } from '~libs.desktop.patient-medical-information';

import { AttachmentsLinks } from '../../shared/AttachmentLinks/AttachmentsLinks';
import { patientMedicalInformationTranslationKeys } from '../../translations';
import { refetchAllMedicalInformation } from '../shared/refetchAllMedicalInformation';
import { DescriptionCell } from '../shared/table/DescriptionCell';
import { MedicalInformationModalOpenLink } from '../shared/table/MedicalInformationModalOpenLink';
import { filterRemovedAttachments } from '../table/MedicalInformationTable.utils';
import { useShowRemovedMedicalInformationState } from '../MedicalInformationShowRemovedCheckbox.hooks';
import { MedicalInformationRemoveLink } from '../shared/table/MedicalInformationRemoveLink';
import { useMedicalInformationRemoveEntryAvailable } from '../shared/useMedicalInformationRemoveEntryAvailable';

import { MedicalImagingDetailsModalOpener } from './details/MedicalImagingDetailsModal.opener';

const DEFAULT_ORDERINGS: Ordering<MedicalImaging>[] = [
   {
      descending: true,
      property: 'medicalInformation',
   },
];

export const useMedicalImagingTableDataConfiguration = (
   patientId: number
): LazyLoadedDataConfigurationByCqrsQuery<MedicalImaging> => {
   const [showRemoved] = useShowRemovedMedicalInformationState();

   return useLazyLoadedDataConfigurationByCqrsQuery<MedicalImaging>(() => {
      const query = new MedicalImagingQuery();
      query.patientId = patientId;
      query.showRemoved = showRemoved;
      return query;
   }, DEFAULT_ORDERINGS);
};

export const useMedicalImagingTableColumns = (
   onRemoveSuccess: VoidFunction
): TableColumnConfiguration<MedicalImaging>[] => {
   const [showRemovedAttachments] = useShowRemovedMedicalInformationState();
   const medicalInformationRemoveEntryAvailable = useMedicalInformationRemoveEntryAvailable();

   return useMemo((): TableColumnConfiguration<MedicalImaging>[] => {
      const columnsWidth = createDynamicColumnsWidth({
         creationDate: '115px',
         entryAuthor: '200px',
         procedure: '235px',
         description: '100%',
         attachments: '276px',
         options: '215px',
      });

      const getBackgroundColor: TableColumnBackgroundColorResolver<MedicalImaging> = (props) =>
         props.bean.medicalInformation.medicalInformationRemoved ? colorsMatrix.red.lighter : undefined;

      return [
         {
            name: 'medicalInformation',
            accessor: (bean) => bean.medicalInformation.creationDate,
            label: patientMedicalInformationTranslationKeys.medicalImaging.table.creationDate,
            width: columnsWidth.creationDate,
            placeholder: TextPlaceholder.NO_DATA,
            backgroundColor: getBackgroundColor,
            formatter: dateFormatter,
         },
         {
            name: 'entryAuthor',
            accessor: noop,
            label: patientMedicalInformationTranslationKeys.medicalImaging.table.entryAuthor,
            width: columnsWidth.entryAuthor,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<MedicalImaging>) => (
               <PractitionerSafePresenter
                  testId={'author'}
                  practitioner={bean.medicalInformation.createdBy}
                  type={'standalone'}
                  withIdentification
               />
            ),
         },
         {
            name: 'procedure',
            accessor: noop,
            label: patientMedicalInformationTranslationKeys.medicalImaging.table.procedure,
            width: columnsWidth.procedure,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<MedicalImaging>) =>
               bean.icd9 ? <span>{`${bean.icd9.getCode()} ${bean.icd9.getName()}`}</span> : null,
         },
         {
            name: 'description',
            accessor: noop,
            label: patientMedicalInformationTranslationKeys.medicalImaging.table.description,
            width: columnsWidth.description,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<MedicalImaging>) => (
               <ConfidentialMask entry={bean.medicalInformation}>
                  {() => {
                     const { description, id } = bean.medicalInformation;

                     if (!description) {
                        return null;
                     }

                     return <DescriptionCell description={description} testId={id} />;
                  }}
               </ConfidentialMask>
            ),
         },
         {
            name: 'attachments',
            accessor: noop,
            label: patientMedicalInformationTranslationKeys.shared.table.attachments,
            width: columnsWidth.attachments,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<MedicalImaging>) => (
               <ConfidentialMask entry={bean.medicalInformation}>
                  {() => {
                     if (!bean.medicalInformation.attachments || bean.medicalInformation.attachments.length === 0) {
                        return null;
                     }

                     return (
                        <MedicalImagingDetailsModalOpener
                           medicalImaging={bean}
                           onMedicalDataAccessAuthorizationUpdate={refetchAllMedicalInformation}
                        >
                           {({ openModal }) => (
                              <AttachmentsLinks
                                 attachments={filterRemovedAttachments(
                                    bean.medicalInformation.attachments!,
                                    Boolean(showRemovedAttachments)
                                 )}
                                 onShowMoreAttachmentsClick={openModal}
                              />
                           )}
                        </MedicalImagingDetailsModalOpener>
                     );
                  }}
               </ConfidentialMask>
            ),
         },
         {
            name: 'options',
            accessor: noop,
            label: '',
            width: columnsWidth.options,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<MedicalImaging>) => {
               const confidentialButton = getConfidentialAccessButton(bean.medicalInformation, {
                  resourceType: 'MEDICAL_INFORMATION',
                  refetchQuery: MedicalImagingQuery,
               });

               if (confidentialButton) {
                  return confidentialButton;
               }

               return (
                  <>
                     <MedicalImagingDetailsModalOpener
                        medicalImaging={bean}
                        onMedicalDataAccessAuthorizationUpdate={refetchAllMedicalInformation}
                     >
                        {({ openModal }) => (
                           <MedicalInformationModalOpenLink
                              onClick={openModal}
                              unsignedAttachmentCount={bean.medicalInformation.unsignedAttachmentCount}
                              size={LinkSize.BIG}
                           />
                        )}
                     </MedicalImagingDetailsModalOpener>

                     {medicalInformationRemoveEntryAvailable(
                        bean.medicalInformation.createdBy,
                        bean.medicalInformation.medicalInformationRemoved
                     ) ? (
                        <PatientMedicalInformationRemoveModalOpener
                           medicalInformationId={bean.medicalInformation.id}
                           onRemoveSuccess={onRemoveSuccess}
                        >
                           {({ openModal }) => <MedicalInformationRemoveLink onClick={openModal} />}
                        </PatientMedicalInformationRemoveModalOpener>
                     ) : null}
                  </>
               );
            },
         },
      ];
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [showRemovedAttachments, medicalInformationRemoveEntryAvailable]);
};
